import React, { useState, useEffect, useCallback, useMemo } from "react";

import {
  Card,
  Table,
  Button,
  Form,
  Badge,
  OverlayTrigger,
  Popover,
  Pagination,
  Spinner,
} from "react-bootstrap";

import Layout from "../../layout/Layout";
import {
  getSevkiyatPlanlamaList,
  getSevkiyatPlanlamaFarkAnaliz,
} from "../../../api/sevkiyat/sevkiyatList";

/* ================= SÜRE HESAPLAMA ================= */
const calculateDuration = (giris, cikis) => {
  if (!giris || !cikis) return "-";

  const [gSaat, gDakika] = giris.split(":").map(Number);
  const [cSaat, cDakika] = cikis.split(":").map(Number);

  if (
    Number.isNaN(gSaat) ||
    Number.isNaN(gDakika) ||
    Number.isNaN(cSaat) ||
    Number.isNaN(cDakika)
  ) {
    return "-";
  }

  let totalDakikaGiris = gSaat * 60 + gDakika;
  let totalDakikaCikis = cSaat * 60 + cDakika;

  if (totalDakikaCikis < totalDakikaGiris) {
    totalDakikaCikis += 24 * 60;
  }

  const farkDakika = totalDakikaCikis - totalDakikaGiris;
  const saat = Math.floor(farkDakika / 60);
  const dakika = farkDakika % 60;

  return `${saat.toString().padStart(2, "0")}:${dakika
    .toString()
    .padStart(2, "0")}`;
};

/* ================= NUMBER HELPER ================= */
const parseNumberSafe = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    String(value).trim() === "" ||
    String(value).trim() === "-"
  ) {
    return null;
  }

  if (typeof value === "number") {
    if (Number.isNaN(value)) return null;
    return value;
  }

  const raw = String(value).trim();

  const cleaned = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  const numberValue = Number(cleaned);

  if (Number.isNaN(numberValue)) return null;

  return numberValue;
};

/* ================= FARK KG HELPERS ================= */
const hasRealFarkValue = (row) => {
  const mosValue = parseNumberSafe(row?.mosCikisKg);
  const farkValue = parseNumberSafe(row?.farkKg);

  if (mosValue === null || mosValue === 0) return false;
  if (farkValue === null || farkValue === 0) return false;

  return true;
};

const getFarkValue = (row) => {
  if (!hasRealFarkValue(row)) return null;

  return parseNumberSafe(row.farkKg);
};

const getFarkAbsValue = (value) => {
  const numberValue = parseNumberSafe(value);

  if (numberValue === null) return null;

  return Math.abs(numberValue);
};

const getFarkType = (farkKg) => {
  const absValue = getFarkAbsValue(farkKg);

  if (absValue === null) return "empty";

  if (absValue <= 200) return "green";

  return "red";
};

const getFarkCellClass = (farkKg) => {
  const type = getFarkType(farkKg);

  if (type === "green") {
    return "bg-success-subtle text-success border-success";
  }

  if (type === "red") {
    return "bg-danger-subtle text-danger border-danger";
  }

  return "";
};

const getFarkFilterText = (filter) => {
  if (filter === "green") return "YEŞİL ≤ 200";
  if (filter === "red") return "KIRMIZI > 200";
  return "TÜMÜ";
};

/* ================= FORMAT HELPERS ================= */
const fmtNum = (num) => {
  const value = Number(num);
  if (!value) return "";
  return value.toLocaleString("tr-TR");
};

const fmtDecimal = (num, digit = 1) => {
  const value = Number(num);
  if (!value) return "0";
  return value.toLocaleString("tr-TR", {
    minimumFractionDigits: digit,
    maximumFractionDigits: digit,
  });
};

const formatDateTR = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatMonthLabel = (donem) => {
  if (!donem) return "";

  const [year, month] = String(donem).split("-");

  const aylar = {
    "01": "Ocak",
    "02": "Şubat",
    "03": "Mart",
    "04": "Nisan",
    "05": "Mayıs",
    "06": "Haziran",
    "07": "Temmuz",
    "08": "Ağustos",
    "09": "Eylül",
    10: "Ekim",
    11: "Kasım",
    12: "Aralık",
  };

  return `${aylar[month] || month} ${year}`;
};

/* ================= ICON ================= */
const FilterIcon = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    fill={active ? "#0d6efd" : "#adb5bd"}
    className="ms-1"
    viewBox="0 0 16 16"
    style={{ cursor: "pointer" }}
  >
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z" />
  </svg>
);

/* ================= EXCEL STYLE FILTER ================= */
const ExcelColumnFilter = ({
  columnKey,
  title,
  options = [],
  activeFilters,
  onApply,
}) => {
  const [tempSelected, setTempSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  const currentFilterValue = activeFilters[columnKey];

  const isFilterActive =
    Array.isArray(currentFilterValue) && currentFilterValue.length > 0;

  const handleEnter = () => {
    setSearchTerm("");

    if (isFilterActive) {
      setTempSelected(currentFilterValue);
    } else {
      setTempSelected(safeOptions);
    }
  };

  const handleCheckboxChange = (val) => {
    if (tempSelected.includes(val)) {
      setTempSelected(tempSelected.filter((item) => item !== val));
    } else {
      setTempSelected([...tempSelected, val]);
    }
  };

  const handleSelectAll = (checked) => {
    const visibleOptions = safeOptions.filter((opt) =>
      opt.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (checked) {
      setTempSelected([...new Set([...tempSelected, ...visibleOptions])]);
    } else {
      if (searchTerm) {
        setTempSelected(
          tempSelected.filter((item) => !visibleOptions.includes(item))
        );
      } else {
        setTempSelected([]);
      }
    }
  };

  const handleApplyClick = () => {
    let nextValue;

    if (
      tempSelected.length === safeOptions.length ||
      tempSelected.length === 0
    ) {
      nextValue = undefined;
    } else {
      nextValue = tempSelected;
    }

    onApply(columnKey, nextValue);
    document.body.click();
  };

  const handleClearClick = () => {
    onApply(columnKey, undefined);
    document.body.click();
  };

  const filteredOptions = safeOptions.filter((opt) =>
    opt.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const popover = (
    <Popover
      id={`popover-${columnKey}`}
      className="shadow"
      style={{ maxWidth: "250px" }}
    >
      <Popover.Body className="p-2">
        <Form.Control
          size="sm"
          placeholder="Ara..."
          className="mb-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />

        <div className="border-bottom pb-2 mb-2">
          <Form.Check
            type="checkbox"
            id={`select-all-${columnKey}`}
            label={<span className="fw-bold small">Tümünü Seç</span>}
            checked={
              filteredOptions.length > 0 &&
              filteredOptions.every((opt) => tempSelected.includes(opt))
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>

        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((val, idx) => (
              <Form.Check
                key={idx}
                type="checkbox"
                id={`chk-${columnKey}-${idx}`}
                label={<span style={{ fontSize: "12px" }}>{val}</span>}
                checked={tempSelected.includes(val)}
                onChange={() => handleCheckboxChange(val)}
                className="mb-1"
              />
            ))
          ) : (
            <div className="text-muted small text-center">Sonuç yok</div>
          )}
        </div>

        <div className="d-flex justify-content-between gap-2 mt-3 border-top pt-2">
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none text-danger p-0"
            style={{ fontSize: "12px" }}
            onClick={handleClearClick}
          >
            Temizle
          </Button>

          <Button
            variant="primary"
            size="sm"
            style={{ fontSize: "11px" }}
            onClick={handleApplyClick}
          >
            Tamam
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      rootClose={true}
      overlay={popover}
      onEnter={handleEnter}
    >
      <span
        className="d-flex align-items-center justify-content-between gap-1"
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {title}
        <FilterIcon active={isFilterActive} />
      </span>
    </OverlayTrigger>
  );
};

/* ================= ANALYSIS COMPONENTS ================= */
const AnalizStatCard = ({ title, value, subText, variant = "light" }) => {
  const bgMap = {
    success: "bg-success text-white",
    danger: "bg-danger text-white",
    primary: "bg-primary text-white",
    warning: "bg-warning text-dark",
    light: "bg-white text-dark",
  };

  return (
    <Card
      className={`border-0 shadow-sm h-100 ${bgMap[variant] || bgMap.light}`}
    >
      <Card.Body>
        <div className="small opacity-75 fw-bold">{title}</div>
        <div className="fs-4 fw-bold mt-1">{value}</div>
        {subText && <div className="small opacity-75 mt-1">{subText}</div>}
      </Card.Body>
    </Card>
  );
};

const MiniAnalizTable = ({ title, rows = [], type }) => {
  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Header className="bg-white fw-bold">{title}</Card.Header>

      <div className="table-responsive">
        <Table
          size="sm"
          bordered
          hover
          className="mb-0 align-middle"
          style={{ fontSize: "12px" }}
        >
          <thead className="table-light">
            <tr>
              <th>{type === "month" ? "Dönem" : "Tarih"}</th>
              <th className="text-end">Kayıt</th>
              <th className="text-end">≤200</th>
              <th className="text-end">&gt;200</th>
              <th className="text-end">Mutlak Fark</th>
              <th className="text-end">Ort.</th>
              <th className="text-end">&gt;200 Oran</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((item, idx) => (
                <tr key={`${type}-${idx}`}>
                  <td className="fw-semibold">
                    {type === "month"
                      ? formatMonthLabel(item.donem)
                      : formatDateTR(item.tarih)}
                  </td>
                  <td className="text-end">{fmtNum(item.toplamKayit)}</td>
                  <td className="text-end text-success fw-bold">
                    {fmtNum(item.ikiYuzAltiAdet)}
                  </td>
                  <td className="text-end text-danger fw-bold">
                    {fmtNum(item.ikiYuzUstuAdet)}
                  </td>
                  <td className="text-end fw-bold">
                    {fmtNum(item.toplamMutlakFarkKg)}
                  </td>
                  <td className="text-end">
                    {fmtDecimal(item.ortalamaMutlakFarkKg)}
                  </td>
                  <td className="text-end">
                    %{fmtDecimal(item.ikiYuzUstuOran)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-muted py-3">
                  Analiz verisi yok.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

const ExtremeRecordTable = ({ title, rows = [] }) => {
  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Header className="bg-white fw-bold">{title}</Card.Header>

      <div className="table-responsive">
        <Table
          size="sm"
          bordered
          hover
          className="mb-0 align-middle"
          style={{ fontSize: "12px" }}
        >
          <thead className="table-light">
            <tr>
              <th>Tarih</th>
              <th>İrsaliye</th>
              <th>Plaka</th>
              <th>Müşteri</th>
              <th>Tip</th>
              <th>Durum</th>
              <th className="text-end">Göktaş KG</th>
              <th className="text-end">MOS KG</th>
              <th className="text-end">Fark</th>
              <th className="text-end">Mutlak</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((item) => (
                <tr key={item._id}>
                  <td>{formatDateTR(item.tarih)}</td>
                  <td>{item.irsaliyeNo}</td>
                  <td className="fw-bold">{item.plaka}</td>
                  <td>{item.musteri}</td>
                  <td>{item.tip}</td>
                  <td>{item.durum}</td>
                  <td className="text-end">{fmtNum(item.goktasCikisKg)}</td>
                  <td className="text-end">{fmtNum(item.mosCikisKg)}</td>
                  <td className="text-end fw-bold text-danger">
                    {fmtNum(item.farkKg)}
                  </td>
                  <td className="text-end fw-bold text-danger">
                    {fmtNum(item.absFarkKg)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center text-muted py-3">
                  Kayıt yok.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

/* ================= MAIN COMPONENT ================= */
function SevkiyatPlanlamaListe() {
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [farkFilter, setFarkFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isAnalizLoading, setIsAnalizLoading] = useState(false);

  const [analizData, setAnalizData] = useState({
    summary: {},
    gunlukAnaliz: [],
    aylikAnaliz: [],
    durumAnaliz: [],
    musteriAnaliz: [],
    ekstremKayitlar: {
      enYuksek200Ustu: [],
      enDusuk200Ustu: [],
    },
  });

  const renderBadge = (status) => {
    const map = {
      SEVK: "success",
      İHRACAT: "primary",
      "FASON DİLME": "info",
      "Y.İÇİ SATIN ALMA": "warning",
      İADE: "danger",
      İTHALAT: "dark",
      BEKLEMEDE: "secondary",
    };

    return (
      <Badge
        bg={map[status] || "secondary"}
        style={{ fontSize: "10px", fontWeight: "500", padding: "4px 6px" }}
      >
        {status || "-"}
      </Badge>
    );
  };

  const filteredRows = useMemo(() => {
    if (farkFilter === "all") return rows;

    return rows.filter((row) => {
      const farkKg = getFarkValue(row);

      if (farkKg === null) return false;

      const type = getFarkType(farkKg);

      if (farkFilter === "green") {
        return type === "green";
      }

      if (farkFilter === "red") {
        return type === "red";
      }

      return true;
    });
  }, [rows, farkFilter]);

  const buildBaseParams = useCallback(
    (overrideFilters = null) => ({
      startDate: dateRange.start,
      endDate: dateRange.end,
      ...(overrideFilters || columnFilters),
    }),
    [dateRange.start, dateRange.end, columnFilters]
  );

  const fetchAnalizData = useCallback(
    async (overrideFilters = null) => {
      setIsAnalizLoading(true);

      try {
        const params = buildBaseParams(overrideFilters);

        const res = await getSevkiyatPlanlamaFarkAnaliz(params);
        const payload = res?.data ? res.data : res;

        if (payload?.success) {
          setAnalizData({
            summary: payload.data?.summary || {},
            gunlukAnaliz: payload.data?.gunlukAnaliz || [],
            aylikAnaliz: payload.data?.aylikAnaliz || [],
            durumAnaliz: payload.data?.durumAnaliz || [],
            musteriAnaliz: payload.data?.musteriAnaliz || [],
            ekstremKayitlar: {
              enYuksek200Ustu:
                payload.data?.ekstremKayitlar?.enYuksek200Ustu || [],
              enDusuk200Ustu:
                payload.data?.ekstremKayitlar?.enDusuk200Ustu || [],
            },
          });
        } else {
          setAnalizData({
            summary: {},
            gunlukAnaliz: [],
            aylikAnaliz: [],
            durumAnaliz: [],
            musteriAnaliz: [],
            ekstremKayitlar: {
              enYuksek200Ustu: [],
              enDusuk200Ustu: [],
            },
          });
        }
      } catch (error) {
        console.error("Sevkiyat fark analiz hatası:", error);

        setAnalizData({
          summary: {},
          gunlukAnaliz: [],
          aylikAnaliz: [],
          durumAnaliz: [],
          musteriAnaliz: [],
          ekstremKayitlar: {
            enYuksek200Ustu: [],
            enDusuk200Ustu: [],
          },
        });
      } finally {
        setIsAnalizLoading(false);
      }
    },
    [buildBaseParams]
  );

  const fetchData = useCallback(
    async (overrideFilters = null, overridePage = null) => {
      setIsLoading(true);

      try {
        const params = {
          page: overridePage || currentPage,
          limit: 20,
          ...buildBaseParams(overrideFilters),
        };

        const res = await getSevkiyatPlanlamaList(params);
        const payload = res?.data ? res.data : res;

        if (payload?.success) {
          setRows(payload.data || []);
          setFilterOptions(payload.filters || {});

          if (payload.pagination) {
            setTotalPages(payload.pagination.totalPage || 1);
            setTotalRecords(payload.pagination.total || 0);
          }
        } else {
          setRows([]);
          setFilterOptions({});
          setTotalPages(1);
          setTotalRecords(0);
        }
      } catch (error) {
        console.error("Sevkiyat planlama liste hatası:", error);
        setRows([]);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, buildBaseParams]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAnalizData();
  }, [fetchAnalizData]);

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...columnFilters, [key]: value };

    if (!value) {
      delete newFilters[key];
    }

    setColumnFilters(newFilters);
    setCurrentPage(1);
    fetchData(newFilters, 1);
    fetchAnalizData(newFilters);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleDateSearch = () => {
    setCurrentPage(1);
    fetchData(columnFilters, 1);
    fetchAnalizData(columnFilters);
  };

  const handleClearAllFilters = () => {
    setDateRange({ start: "", end: "" });
    setColumnFilters({});
    setFarkFilter("all");
    setCurrentPage(1);

    fetchData({}, 1);
    fetchAnalizData({});
  };

  const handleFarkHeaderClick = () => {
    setFarkFilter((prev) => {
      if (prev === "all") return "green";
      if (prev === "green") return "red";
      return "all";
    });
  };

  const summary = analizData.summary || {};
  const ekstremKayitlar = analizData.ekstremKayitlar || {};

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Sevkiyat Planlama</h4>
          <small className="text-muted">
            Toplam {totalRecords} kayıt bulundu.
          </small>

          {farkFilter !== "all" && (
            <div className="small text-muted mt-1">
              FARK KG filtresi: <b>{getFarkFilterText(farkFilter)}</b> |
              Gösterilen: <b>{filteredRows.length}</b>
            </div>
          )}
        </div>

        <div className="d-flex gap-2 align-items-end bg-white p-2 rounded shadow-sm border">
          <div>
            <label className="small text-muted d-block fw-bold">
              Başlangıç
            </label>

            <Form.Control
              type="date"
              size="sm"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
            />
          </div>

          <div>
            <label className="small text-muted d-block fw-bold">Bitiş</label>

            <Form.Control
              type="date"
              size="sm"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
            />
          </div>

          <Button
            size="sm"
            variant="primary"
            onClick={handleDateSearch}
            disabled={isLoading || isAnalizLoading}
          >
            Filtrele
          </Button>

          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearAllFilters}
            disabled={isLoading || isAnalizLoading}
            title="Temizle"
          >
            ↺
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <div className="table-responsive">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Yükleniyor...</p>
            </div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{ fontSize: "12px", whiteSpace: "nowrap", color: "#333" }}
            >
              <thead className="table-light">
                <tr style={{ height: "40px" }}>
                  <th>SIRA</th>
                  <th style={{ width: "70px" }}>TARİH</th>
                  <th>İRSALİYE NO</th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="plaka"
                      title="PLAKA"
                      options={filterOptions.plaka}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="sevkYeri"
                      title="SEVK YERİ"
                      options={filterOptions.sevkYeri}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="musteri"
                      title="MÜŞTERİ"
                      options={filterOptions.musteri}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="cinsi"
                      title="CİNSİ"
                      options={filterOptions.cinsi}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="tip"
                      title="SOĞUK/GALVANİZ"
                      options={filterOptions.tip}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th className="text-end">ADET</th>
                  <th className="text-end fw-bold text-dark">FİRMA GİRİŞ KG</th>
                  <th className="text-end fw-bold text-dark">
                    GÖKTAŞ ÇIKIŞ KG
                  </th>
                  <th className="text-end fw-bold text-dark">MOS ÇIKIŞ KG</th>

                  <th
                    className={`text-end fw-bold ${
                      farkFilter !== "all" ? "table-active" : ""
                    }`}
                    onClick={handleFarkHeaderClick}
                    title="Tıklayınca sırasıyla: Tümü → Yeşil → Kırmızı"
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    FARK KG{" "}
                    <Badge
                      bg={
                        farkFilter === "green"
                          ? "success"
                          : farkFilter === "red"
                            ? "danger"
                            : "secondary"
                      }
                      style={{ fontSize: "9px" }}
                    >
                      {getFarkFilterText(farkFilter)}
                    </Badge>
                  </th>

                  <th className="text-center">
                    <ExcelColumnFilter
                      columnKey="durum"
                      title="DURUM"
                      options={filterOptions.durum}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="nakliye"
                      title="NAKLİYE"
                      options={filterOptions.nakliye}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>FATURA NO</th>
                  <th>ARAÇ GİRİŞ</th>
                  <th>ARAÇ ÇIKIŞ</th>
                  <th>SÜRE</th>
                  <th>KONTEYNER NO</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((r) => {
                    const goktasKg = parseNumberSafe(r.goktasCikisKg) || 0;
                    const mosKg = parseNumberSafe(r.mosCikisKg) || 0;
                    const farkKg = getFarkValue(r);

                    return (
                      <tr key={r._id}>
                        <td className="px-2">{r.sira}</td>
                        <td className="px-2">{formatDateTR(r.tarih)}</td>
                        <td className="px-2">{r.irsaliyeNo}</td>
                        <td className="px-2 fw-bold">{r.plaka}</td>

                        <td
                          className="px-2"
                          style={{
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={r.sevkYeri}
                        >
                          {r.sevkYeri}
                        </td>

                        <td
                          className="px-2 fw-semibold"
                          style={{
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={r.musteri}
                        >
                          {r.musteri}
                        </td>

                        <td className="px-2">{r.cinsi}</td>
                        <td className="px-2">{r.tip}</td>
                        <td className="text-end px-2">{fmtNum(r.adet)}</td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(r.firmaGirisKg)}
                        </td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(goktasKg)}
                        </td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(mosKg)}
                        </td>

                        <td
                          className={`text-end px-2 fw-bold ${
                            farkKg !== null
                              ? `border ${getFarkCellClass(farkKg)}`
                              : ""
                          }`}
                        >
                          {farkKg !== null ? fmtNum(farkKg) : ""}
                        </td>

                        <td className="text-center px-2">
                          {renderBadge(r.durum)}
                        </td>

                        <td className="px-2">{r.nakliye}</td>
                        <td className="px-2">{r.faturaNo}</td>
                        <td className="px-2">{r.aracGirisSaat}</td>
                        <td className="px-2">{r.aracCikisSaat}</td>

                        <td className="px-2 fw-bold text-primary">
                          {r.sureFark ||
                            calculateDuration(r.aracGirisSaat, r.aracCikisSaat)}
                        </td>

                        <td className="px-2">{r.konteynerNo}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="19" className="text-center py-4">
                      Kayıt bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && rows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border-top">
            <small className="text-muted">
              Toplam <b>{totalRecords}</b> kayıttan{" "}
              <b>
                {(currentPage - 1) * 20 + 1} -{" "}
                {Math.min(currentPage * 20, totalRecords)}
              </b>{" "}
              arası.
              {farkFilter !== "all" && (
                <>
                  {" "}
                  | FARK KG filtresiyle gösterilen: <b>{filteredRows.length}</b>
                </>
              )}
            </small>

            <Pagination size="sm" className="m-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />

              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;

                if (
                  totalPages > 10 &&
                  Math.abs(currentPage - pageNum) > 3 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  return null;
                }

                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />

              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card>

      {/* ================= ANALİZLER ================= */}
      <div className="mb-3">
        <h5 className="fw-bold text-primary mb-1">Fark KG Analizleri</h5>
        <small className="text-muted">
          Analize yalnızca MOS çıkış kg ve FARK KG değeri dolu olan kayıtlar
          dahil edilir.
        </small>
      </div>

      {isAnalizLoading ? (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Analiz verileri yükleniyor...</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <AnalizStatCard
                title="Analize Giren Kayıt"
                value={fmtNum(summary.toplamKayit)}
                subText="MOS ve FARK dolu kayıtlar"
                variant="primary"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="200 ve Altı"
                value={fmtNum(summary.ikiYuzAltiAdet)}
                subText={`Oran: %${fmtDecimal(summary.ikiYuzAltiOran)}`}
                variant="success"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="200 Üstü"
                value={fmtNum(summary.ikiYuzUstuAdet)}
                subText={`Oran: %${fmtDecimal(summary.ikiYuzUstuOran)}`}
                variant="danger"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="Toplam Mutlak Fark"
                value={`${fmtNum(summary.toplamMutlakFarkKg)} kg`}
                subText={`Net fark: ${fmtNum(summary.toplamNetFarkKg)} kg`}
                variant="warning"
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <AnalizStatCard
                title="Ortalama Mutlak Fark"
                value={`${fmtDecimal(summary.ortalamaMutlakFarkKg)} kg`}
                subText="Kayıt başına ortalama sapma"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="En Yüksek Mutlak Fark"
                value={`${fmtNum(summary.enYuksekMutlakFarkKg)} kg`}
                subText="Tüm analiz kayıtları içinde"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="200 Altı Toplam Fark"
                value={`${fmtNum(summary.ikiYuzAltiToplamMutlakFarkKg)} kg`}
                subText="Yeşil grup toplamı"
                variant="success"
              />
            </div>

            <div className="col-md-3">
              <AnalizStatCard
                title="200 Üstü Toplam Fark"
                value={`${fmtNum(summary.ikiYuzUstuToplamMutlakFarkKg)} kg`}
                subText="Kırmızı grup toplamı"
                variant="danger"
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <MiniAnalizTable
                title="Günlük Fark Analizi"
                rows={analizData.gunlukAnaliz}
                type="day"
              />
            </div>

            <div className="col-lg-6">
              <MiniAnalizTable
                title="Aylık Fark Analizi"
                rows={analizData.aylikAnaliz}
                type="month"
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <ExtremeRecordTable
                title="200 Üstü En Yüksek Fark Kayıtları"
                rows={ekstremKayitlar.enYuksek200Ustu || []}
              />
            </div>

            <div className="col-lg-6">
              <ExtremeRecordTable
                title="200 Üstü En Düşük Fark Kayıtları"
                rows={ekstremKayitlar.enDusuk200Ustu || []}
              />
            </div>
          </div>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white fw-bold">
              Müşteri Bazlı 200 Üstü Fark Analizi
            </Card.Header>

            <div className="table-responsive">
              <Table
                bordered
                hover
                size="sm"
                className="mb-0 align-middle"
                style={{ fontSize: "12px" }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Müşteri</th>
                    <th className="text-end">Kayıt</th>
                    <th className="text-end">Toplam Mutlak Fark</th>
                    <th className="text-end">Ortalama</th>
                    <th className="text-end">En Yüksek</th>
                  </tr>
                </thead>

                <tbody>
                  {analizData.musteriAnaliz.length > 0 ? (
                    analizData.musteriAnaliz.map((item, idx) => (
                      <tr key={idx}>
                        <td className="fw-semibold">{item.musteri || "-"}</td>
                        <td className="text-end">{fmtNum(item.toplamKayit)}</td>
                        <td className="text-end fw-bold text-danger">
                          {fmtNum(item.toplamMutlakFarkKg)}
                        </td>
                        <td className="text-end">
                          {fmtDecimal(item.ortalamaMutlakFarkKg)}
                        </td>
                        <td className="text-end fw-bold text-danger">
                          {fmtNum(item.enYuksekMutlakFarkKg)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        Müşteri analiz verisi yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
}

export default SevkiyatPlanlamaListe;
